import { Button } from '@mui/material';
import { Dictionary, debounce, isEmpty, pick } from 'lodash';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { StoreState } from '~/reducer';
import { userSettingsStoreStateSelector } from '~/selectors';
import { IAm, PreferencesApiClient } from '~/services/Http';

export const HandleUserSettingsUpdate: React.FC = () => {
  const userSettingsRef = React.useRef<Dictionary<object>>();
  const userSettingsToBeUpdatedRef = React.useRef<Dictionary<object>>({});
  const [failedToSave, setFailedToSave] = React.useState(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const retrySaveSnackbarKeyRef = React.useRef<string | number>();
  const { t } = useTranslation();

  const isClearingAllUserSettings = useSelector((state: StoreState) => {
    return userSettingsStoreStateSelector(state).isClearingAllUserSettings;
  });

  const userSettings = useSelector((state: StoreState) => {
    return userSettingsStoreStateSelector(state).userSettings;
  });

  const handleUserSettingsSave = React.useCallback(() => {
    if (!isEmpty(userSettingsToBeUpdatedRef.current)) {
      const pendingUserSettings = userSettingsToBeUpdatedRef.current;
      userSettingsToBeUpdatedRef.current = {};

      (async () => {
        try {
          setFailedToSave(false);

          await PreferencesApiClient.setPreferences(
            IAm.UserPreferencesInputDto.fromJS({
              data: pendingUserSettings,
            }),
          );
        } catch {
          setFailedToSave(true);

          if (userSettingsRef.current) {
            userSettingsToBeUpdatedRef.current = {
              ...userSettingsToBeUpdatedRef.current,
              ...pick(
                userSettingsRef.current,
                Object.keys(pendingUserSettings),
              ),
            };
          }
        }
      })();
    }
  }, [userSettingsRef, userSettingsToBeUpdatedRef, setFailedToSave]);

  const handleUserSettingsSaveDebounced = React.useMemo(() => {
    return debounce(handleUserSettingsSave, 5000);
  }, [handleUserSettingsSave]);

  const handleSaveUserSettingsInstantly = React.useCallback(() => {
    handleUserSettingsSaveDebounced.cancel();
    handleUserSettingsSave();
  }, [handleUserSettingsSaveDebounced, handleUserSettingsSave]);

  React.useEffect(() => {
    /**
     * Includes all the updated settings except the ones
     * which are undefined or currently clearing.
     */
    const nextUserSettings: Dictionary<object> = (() => {
      if (isClearingAllUserSettings) {
        return {};
      }

      return Object.keys(userSettings).reduce((accumulator, key) => {
        if (
          !userSettings[key].data ||
          !userSettings[key].isUpdated ||
          userSettings[key].isClearing
        ) {
          return accumulator;
        }

        return {
          ...accumulator,
          [key]: userSettings[key].data,
        };
      }, {});
    })();

    if (userSettingsRef.current) {
      /**
       * Discard all the settings that were deleted
       * or which are being deleted.
       */
      userSettingsToBeUpdatedRef.current = {
        ...pick(
          userSettingsToBeUpdatedRef.current,
          Object.keys(nextUserSettings),
        ),
        ...pick(
          nextUserSettings,
          Object.keys(nextUserSettings).filter(
            (key) => nextUserSettings[key] !== userSettingsRef.current?.[key],
          ),
        ),
      };
    }

    userSettingsRef.current = nextUserSettings;
    handleUserSettingsSaveDebounced();
  }, [
    isClearingAllUserSettings,
    userSettings,
    userSettingsRef,
    userSettingsToBeUpdatedRef,
    handleUserSettingsSaveDebounced,
  ]);

  /**
   * Make sure to save instantly all the pending changes
   * when un-mounting the component or when leaving the page.
   */
  React.useEffect(() => {
    window.addEventListener('beforeunload', handleSaveUserSettingsInstantly);

    return () => {
      handleSaveUserSettingsInstantly();

      window.removeEventListener(
        'beforeunload',
        handleSaveUserSettingsInstantly,
      );
    };
  }, [handleSaveUserSettingsInstantly]);

  const handleCloseRetrySaveSnackbar = React.useCallback(() => {
    if (retrySaveSnackbarKeyRef.current) {
      closeSnackbar(retrySaveSnackbarKeyRef.current);
    }
  }, [retrySaveSnackbarKeyRef, closeSnackbar]);

  const handleInstantlySaveAndCloseSnackbar = React.useCallback(() => {
    handleSaveUserSettingsInstantly();
    handleCloseRetrySaveSnackbar();
  }, [handleSaveUserSettingsInstantly, handleCloseRetrySaveSnackbar]);

  /**
   * Enqueue snackbar to let the user retry to manually
   * save user settings in case it failed.
   */
  React.useEffect(() => {
    if (failedToSave) {
      retrySaveSnackbarKeyRef.current = enqueueSnackbar(
        t('failedToSaveUserSettings'),
        {
          persist: true,
          variant: 'error',
          action: () => (
            <Button
              color="inherit"
              onClick={handleInstantlySaveAndCloseSnackbar}
            >
              {t('tryAgain')}
            </Button>
          ),
        },
      );
    } else {
      handleCloseRetrySaveSnackbar();
    }
  }, [
    t,
    failedToSave,
    retrySaveSnackbarKeyRef,
    enqueueSnackbar,
    handleInstantlySaveAndCloseSnackbar,
    handleCloseRetrySaveSnackbar,
  ]);

  /**
   * Make sure to close the snackbar if it still remains
   * opened before unmount.
   */
  React.useEffect(() => {
    return () => {
      handleCloseRetrySaveSnackbar();
    };
  }, [handleCloseRetrySaveSnackbar]);

  return null;
};
